import { t } from "@lingui/macro";
import _ from "lodash";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as yup from "yup";

import { CheckBox } from "@whoosh/design-system/src/components/controls/CheckBox";
import {
  DropdownSelect,
  DropdownSelectOption,
} from "@whoosh/design-system/src/components/controls/DropdownSelect";
import { Input } from "@whoosh/design-system/src/components/controls/Input";
import { TextArea } from "@whoosh/design-system/src/components/controls/TextArea";
import { MarkdownDisplay } from "@whoosh/design-system/src/components/display/MarkdownDisplay/MarkdownDisplay";
import { GetTransportIconFromCode } from "@whoosh/design-system/src/components/icons_v2/transportation";

import { HStack, Pressable, Text, VStack } from "../../primitive";
import { PhoneInput } from "../PhoneInput";
import {
  decodeCheckboxSelection,
  encodeCheckboxSelection,
} from "./checkboxSelection";

export type FormQuestionConfiguration = {
  id?: string;
  enabled?: boolean;
  guestVisibility?: "NONE" | "OPTIONAL" | "REQUIRED";
  memberVisibility?: "NONE" | "OPTIONAL" | "REQUIRED";
  placeholder?: string;
  prompt?: string;
  description?: string | null;
  shortName?: string | null;
  target?: "GROUP" | "PLAYER";
  type?:
    | "BOOLEAN"
    | "CHECKBOXES"
    | "GHIN_NUMBER"
    | "LONG_TEXT"
    | "MULTIPLE_CHOICE"
    | "TEXT"
    | "TOGGLE"
    | "TRANSPORTATION"
    | "NAME"
    | "PHONE_NUMBER"
    | "EMAIL";
  values?: (string | null | undefined)[] | null | undefined;
};

export type EventPlayer = {
  id?: string | null;
  playerId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  detail?: string;
  profileImage?: {
    publicId: string;
  } | null;
  phoneNumber?: string | null;
  ghinNumber?: number | null;
};

type EventFormInputRendererProps = FormQuestionConfiguration & {
  onChange: ({
    id,
    value,
    booleanValue,
    transportationTypeId,
  }: {
    id: string;
    value?: string;
    booleanValue?: boolean;
    transportationTypeId?: string;
  }) => void;
  id: string;
  value?: string;
  booleanValue?: boolean;
  transportationTypeId?: string;
  transporationTypes?: { id: string; code: string; customCode?: string }[];
  required?: boolean;
  registrant?: EventPlayer;
};

export const EventFormInputRenderer = ({
  type,
  prompt,
  description,
  values,
  onChange,
  id,
  value,
  booleanValue,
  required,
  transportationTypeId,
  transporationTypes,
  registrant,
}: EventFormInputRendererProps) => {
  const [localValues, setLocalValues] = useState({
    value,
    booleanValue,
    transportationTypeId,
  });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    value: localValue,
    booleanValue: localBooleanValue,
    transportationTypeId: localTransportationTypeId,
  } = localValues;

  // Use useRef to persist the debounced function across renders
  const debouncedHandleChangeRef = useRef<
    _.DebouncedFunc<typeof onChange> | undefined
  >(undefined);

  if (!debouncedHandleChangeRef.current) {
    debouncedHandleChangeRef.current = _.debounce(onChange, 300);
  }

  // Update the debounced function when onChange changes
  useEffect(() => {
    debouncedHandleChangeRef.current?.cancel();
    debouncedHandleChangeRef.current = _.debounce(onChange, 300);

    // Cleanup on unmount
    return () => {
      debouncedHandleChangeRef.current?.cancel();
    };
  }, [onChange]);

  // Sync local state when external values change (e.g., from prefilling)
  useEffect(() => {
    setLocalValues({
      value,
      booleanValue,
      transportationTypeId,
    });
  }, [value, booleanValue, transportationTypeId]);

  const localOnChange = useCallback(
    ({
      id,
      value,
      booleanValue,
      transportationTypeId,
      emitChange = true,
    }: {
      id: string;
      value?: string;
      booleanValue?: boolean;
      transportationTypeId?: string;
      emitChange?: boolean;
    }) => {
      setLocalValues({ value, booleanValue, transportationTypeId });
      if (emitChange) {
        debouncedHandleChangeRef.current?.({
          id,
          value,
          booleanValue,
          transportationTypeId,
        });
      }
    },
    []
  );

  const input = useMemo(() => {
    switch (type) {
      case "GHIN_NUMBER":
      case "TEXT":
        return (
          <Input
            name={id}
            isDisabled={type === "GHIN_NUMBER" && !!registrant?.ghinNumber}
            value={localValue ?? ""}
            placeholder={t`Enter an Answer`}
            onChange={(value) => {
              if (typeof value === "string") {
                localOnChange({ id, value });
              }
            }}
          />
        );
      case "NAME":
        return (
          <Input
            name={id}
            value={localValue ?? ""}
            isDisabled={!!registrant?.firstName && !!registrant?.lastName}
            placeholder={t`Enter a Name`}
            onChange={(value) => {
              if (typeof value === "string") {
                localOnChange({ id, value });
              }
            }}
          />
        );
      case "EMAIL":
        return (
          <Input
            name={id}
            testID={`email-form-input`}
            placeholder={t`Enter your Email`}
            value={localValue ?? ""}
            isDisabled={!!registrant?.email && registrant.email === localValue}
            onBlur={() => {
              const emailSchema = yup.string().email();
              emailSchema
                .validate(localValue)
                .then(() => {
                  setErrorMessage(null);
                })
                .catch(() => {
                  setErrorMessage("(Must be a valid email address)");
                });
            }}
            onChange={(value) => {
              const emailSchema = yup.string().email();
              emailSchema
                .validate(value)
                .then(() => {
                  if (typeof value === "string") {
                    localOnChange({ id, value });
                    setErrorMessage(null);
                  }
                })
                .catch(() => {
                  if (typeof value === "string") {
                    localOnChange({ id, value });
                  }
                });
            }}
          />
        );

      case "BOOLEAN":
        return (
          <DropdownSelect
            placeholder={t`Select Answer`}
            value={localBooleanValue?.toString() ?? ""}
            options={[
              {
                value: "true",
                label: t`Yes`,
              },
              {
                value: "false",
                label: t`No`,
              },
            ]}
            onChange={(value) =>
              localOnChange({
                id,
                booleanValue: value === "true" ? true : false,
              })
            }
          />
        );

      case "LONG_TEXT":
        return (
          <TextArea
            name={id}
            accessibilityLabel={prompt}
            placeholder={t`Enter an Answer`}
            onChange={(value) => localOnChange({ id, value })}
            value={localValue}
            maxLength={255}
            isRequired={required}
          />
        );

      case "CHECKBOXES":
        return (
          <VStack gap={2}>
            {values?.map((v, i) => (
              <CheckBox
                key={i}
                id={`${id}-${i}`}
                label={v ?? ""}
                isChecked={
                  decodeCheckboxSelection(localValue, values)?.includes(
                    `${v}`
                  ) ?? false
                }
                testID={`checkbox-${v}`}
                onPress={() => {
                  const currentValues =
                    decodeCheckboxSelection(localValue, values) ?? [];

                  const valueStr = `${v}`;
                  const newValues = currentValues.includes(valueStr)
                    ? currentValues.filter((val) => val !== valueStr)
                    : [...currentValues, valueStr];

                  localOnChange({
                    id,
                    value: encodeCheckboxSelection(newValues),
                  });
                }}
              />
            ))}
          </VStack>
        );
      case "MULTIPLE_CHOICE":
        const options = values?.map(
          (v) =>
            ({
              label: v ?? "",
              value: v,
            } as DropdownSelectOption<string>)
        );
        return (
          <DropdownSelect
            placeholder={t`Select Answer`}
            value={localValue ?? ""}
            options={options ?? []}
            onChange={(value) => {
              if (value) {
                localOnChange({ id, value });
              }
            }}
          />
        );
      case "TRANSPORTATION":
        const transportationOptions = transporationTypes?.filter((t) =>
          values?.includes(t.id)
        );

        return (
          <HStack gap={4}>
            {transportationOptions?.map((t, index) => {
              const TransportationIcon = t.code
                ? GetTransportIconFromCode(t.code)
                : null;
              const active = localTransportationTypeId === t.id;

              return (
                <Pressable
                  key={t.id}
                  height={44}
                  width={44}
                  borderWidth={2}
                  borderRadius="2"
                  justifyContent={"center"}
                  alignItems={"center"}
                  testID={`pressable-transport-${index}`}
                  borderColor={active ? "main-primary-500" : "surface-dividers"}
                  backgroundColor={
                    active ? "main-primary-500" : "surface-primary"
                  }
                  onPress={() => {
                    localOnChange({ id, transportationTypeId: t.id });
                  }}
                >
                  {TransportationIcon ? (
                    <TransportationIcon color={active ? "white" : "black"} />
                  ) : (
                    <Text color={active ? "surface-primary" : "text-primary"}>
                      {t.customCode || ""}
                    </Text>
                  )}
                </Pressable>
              );
            })}
          </HStack>
        );

      case "PHONE_NUMBER":
        return (
          <PhoneInput
            testID={`phone-form-input`}
            nationalNumber={localValue ?? ""}
            name={id}
            isDisabled={!!registrant?.phoneNumber}
            placeholder={t`Enter your Phone Number`}
            defaultValue={localValue ?? ""}
            onChange={(value) => {
              localOnChange({ id, value: value.nationalNumber });
            }}
          />
        );
      default:
        return null;
    }
  }, [
    type,
    id,
    localValue,
    localBooleanValue,
    localTransportationTypeId,
    values,
    registrant,
    transporationTypes,
    localOnChange,
    prompt,
    required,
  ]);

  return (
    <VStack width="100%" gap={2} mb={4}>
      <VStack gap={2}>
        <HStack flexWrap="wrap" alignItems="center" gap={1}>
          <Text variant="mainBaseMedium" color="text-primary">
            {prompt}{" "}
          </Text>
          {errorMessage ? (
            <Text variant="mainSmall" color={"text-error"}>
              {errorMessage}
            </Text>
          ) : required ? (
            <Text
              variant="mainSmall"
              color={"text-secondary"}
            >{t`(required)`}</Text>
          ) : null}
        </HStack>
        {input}
        {description ? <MarkdownDisplay markdown={description} /> : null}
      </VStack>
    </VStack>
  );
};
